import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface PlatformAdmin {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  twoFaEnabled: boolean;
}

interface PlatformAuthState {
  accessToken: string | null;
  admin: PlatformAdmin | null;
  isHydrated: boolean;
  setSession: (data: { accessToken: string; admin: PlatformAdmin }) => void;
  setAccessToken: (token: string) => void;
  /**
   * Fold a change to the operator's OWN account into the session the shell renders from.
   *
   * The sidebar footer and the topbar both read this store, not the query cache, so without it
   * an operator renames themselves in My Account and the console keeps calling them the old
   * name until they sign in again.
   */
  patchAdmin: (patch: Partial<PlatformAdmin>) => void;
  clear: () => void;
  setHydrated: () => void;
}

/**
 * Platform auth store (Super Admin Console).
 *
 * The access token is held in memory only (not persisted) for security.
 * Admin profile is persisted to localStorage so the shell can render on
 * reload — but the access token is re-acquired via /auth/refresh on first
 * network call (cookie-based, same pattern as chambers auth).
 */
export const usePlatformAuthStore = create<PlatformAuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      admin: null,
      isHydrated: false,
      setSession: ({ accessToken, admin }) => set({ accessToken, admin }),
      setAccessToken: (token) => set({ accessToken: token }),
      patchAdmin: (patch) =>
        set((state) => (state.admin ? { admin: { ...state.admin, ...patch } } : {})),
      clear: () => set({ accessToken: null, admin: null }),
      setHydrated: () => set({ isHydrated: true }),
    }),
    {
      name: 'jurivoxa-platform-auth',
      // Only persist the admin profile — never the access token
      partialize: (state) => ({
        admin: state.admin,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
    },
  ),
);
